import { push } from 'connected-react-router';
import { setAuthentication } from '../redux/auth/actions';
import { addNotification } from '../redux/ui/notifications/actions';
import { getTokenSubject } from './refreshTokenSession';
import {
  getLoginSession,
  isLoginSession,
  LOGIN_SESSION_STORAGE_KEY,
  LoginSession,
  sameLoginSession
} from './loginSession';
import {
  isProductionMonitoringAccessDenied,
  NO_ACCESS_MESSAGE,
  NO_ACCESS_NOTIFICATION_KEY
} from '../attrs/accessErrors';
import { NOTIFICATION_TYPE_INTERACTION } from '../attrs/notifications';
import { ERROR } from '../attrs/status';

type SessionStore = {
  getState: () => {
    auth: { isAuthenticated: boolean; session: LoginSession | null };
    user: { user: { data: { id?: string } | null } };
  };
  dispatch: (action: unknown) => unknown;
};

export const ACCESS_DENIED_STORAGE_KEY = 'productionMonitoringAccessDenied';

const logoutLocalSession = (store: SessionStore): void => {
  // Shared credentials may belong to another login; only close this tab's sockets.
  store.dispatch(setAuthentication(false, { localOnly: true }));
  store.dispatch(push('/login'));
};

const endLocalSessionWithoutAccess = (requestSession: LoginSession, store: SessionStore): void => {
  // Shared storage may already be empty, but each tab must end its own session.
  const state = store.getState();
  if (
    !state.auth.isAuthenticated ||
    state.user.user.data?.id !== requestSession.subject ||
    !sameLoginSession(state.auth.session, requestSession)
  )
    return;

  logoutLocalSession(store);
  store.dispatch(
    addNotification({
      key: NO_ACCESS_NOTIFICATION_KEY,
      type: ERROR,
      message: NO_ACCESS_MESSAGE,
      notificationType: NOTIFICATION_TYPE_INTERACTION
    })
  );
};

export const subscribeToAccessDenials = (store: SessionStore): (() => void) => {
  const onStorage = (event: StorageEvent) => {
    if (
      event.storageArea !== localStorage ||
      ![ACCESS_DENIED_STORAGE_KEY, LOGIN_SESSION_STORAGE_KEY].includes(event.key || '') ||
      !event.newValue
    )
      return;

    let message;
    try {
      message = JSON.parse(event.newValue);
    } catch {
      return;
    }
    if (!isLoginSession(message)) return;

    if (event.key === LOGIN_SESSION_STORAGE_KEY) {
      const state = store.getState();
      if (
        !state.auth.isAuthenticated ||
        state.user.user.data?.id !== message.subject ||
        !state.auth.session ||
        state.auth.session.subject !== message.subject ||
        sameLoginSession(state.auth.session, message)
      )
        return;

      let previousSession;
      try {
        previousSession = JSON.parse(event.oldValue || 'null');
      } catch {
        previousSession = null;
      }
      // A queued event may outlive the shared session (e.g. a subsequent denial).
      // In that case it can only invalidate the exact login it replaced.
      if (
        sameLoginSession(getLoginSession(), message) ||
        (isLoginSession(previousSession) && sameLoginSession(state.auth.session, previousSession))
      )
        logoutLocalSession(store);
      return;
    }

    // Receivers only end their own session; they never clear storage or rebroadcast.
    endLocalSessionWithoutAccess(message, store);
  };

  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
};

export const endSessionWithoutAccess = (
  error: unknown,
  requestSession: LoginSession | null,
  store: SessionStore
): void => {
  if (!isProductionMonitoringAccessDenied(error) || !requestSession) return;

  const tokens = [localStorage.getItem('accessToken'), localStorage.getItem('refreshToken')].filter(Boolean);
  if (
    sameLoginSession(getLoginSession(), requestSession) &&
    tokens.length > 0 &&
    tokens.every(token => getTokenSubject(token) === requestSession.subject)
  ) {
    // Clear shared WebSocket credentials only while this user owns the session.
    localStorage.removeItem('websocketToken');
    localStorage.removeItem('loadingWebsocketTokenLock');
    localStorage.removeItem(LOGIN_SESSION_STORAGE_KEY);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  endLocalSessionWithoutAccess(requestSession, store);
  try {
    localStorage.setItem(ACCESS_DENIED_STORAGE_KEY, JSON.stringify(requestSession));
    // Keep this as a transient event, not a denial to replay after a future login.
    localStorage.removeItem(ACCESS_DENIED_STORAGE_KEY);
  } catch {
    // Local logout has already completed even if cross-tab messaging is unavailable.
  }
};
